import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { updateTextMesh } from '../../../utils/updateTextMesh';
import * as THREE from 'three';
import { createLightFixture } from '../../../factory/symbols/createLightFixtureSymbols';
import { useDragHandler } from '../../../utils/useDragHandler';

// eslint-disable-next-line no-restricted-imports -- CSS paylaşımlı, components/TypeMenus/'ta kaldı
import '../../../components/TypeMenus/PanelTypeMenus.css'
export default function FixtureTypeMenu({
  x,
  y,
  value,
  onClose,
  onAddPositionChange,
  onActionSelect,
  fixtureDetails,
  onDrag,
  scene,
}) {
  const settings = useSelector((state) => state.settings);

  let fixture
  let textGroup
  let textType
  let textPower
  let textDesc
  let fixtureUserData
  scene.traverse(obj => {
    if (obj.userData.id === fixtureDetails.ID) {
      fixture = obj;
      fixtureUserData = obj.userData
      // textGroup’u name ile bulalım
      textGroup = fixture.getObjectByName('textGroup');

      if (textGroup) {
        // sprite’ları da name’leri üzerinden yakalayalım
        textType = textGroup.getObjectByName('textType');
        textPower = textGroup.getObjectByName('textPower');
        textDesc = textGroup.getObjectByName('textDesc');
      }
    }
  });

  const [details, setDetails] = useState({
    fixtureType: fixtureUserData ? fixtureUserData.fixtureType : "N",
    lampCount: fixtureUserData ? fixtureUserData.lampCount : 1,
    lampPower: fixtureUserData ? fixtureUserData.lampPower : 0,
    power: fixtureUserData ? fixtureUserData.power : 0,
    symbol: fixtureUserData ? fixtureUserData.symbol : "normal",
  })
  const menuRef = useRef(null);

  const { handleMouseDown } = useDragHandler(onDrag);

  const handleMouseDownWithStop = (e) => {
    e.stopPropagation();
    handleMouseDown(e);
  };

  const handleSelectSymbol = (e) => {
    const newSymbolType = e.target.value;

    // 1) Mevcut fixture objesini bulun
    if (!fixture) return;
    const oldFixture = fixture;

    // 2) Eski userData'yı kaydedin (tip/güç/açıklama dahil — yeni fixture'a taşınacak)
    const {
      id, tempLineUUID, power,
      fixtureType: oldType, lampPower: oldLampPower, lampCount: oldLampCount,
      description: oldDesc, basePoint: oldBasePoint,
    } = oldFixture.userData;

    // ✅ Mevcut rotasyonu al - remove/dispose'dan ÖNCE (userData.rotate yoksa rotation.z)
    let currentRotation = oldFixture.userData.rotate;
    if (currentRotation === undefined || currentRotation === null) {
      currentRotation = oldFixture.rotation.z;
    }

    // Use fixture symbol color from settings (fallback to white)
    const fixtureColorHex = settings?.fixture?.color || '#ffffff';
    const fixtureColorNum = typeof fixtureColorHex === 'string' ? parseInt(fixtureColorHex.replace('#', ''), 16) : fixtureColorHex;

    // 3) GERÇEK parent'tan kaldır + belleği temizle.
    // KÖK NEDEN: fixture nested olabilir (fpMesh/documentGroup). scene.remove(oldFixture)
    // nested'da NO-OP'tu → eski şekil sahnede kalıp yeni fixture'la üst üste biniyordu.
    // Ayrıca createLightFixture(scene,...) yeniyi scene ROOT'a + world basePoint'e ekliyordu
    // (kat planından kopuk). Çözüm: aynı parent + aynı LOCAL konum.
    const parent = oldFixture.parent || scene;
    const localPos = oldFixture.position.clone();
    parent.remove(oldFixture);
    oldFixture.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });

    // 4) Yeni fixture'ı AYNI parent'a, AYNI local konuma ekle (scene=null → auto-add yok)
    const newFixture = createLightFixture(
      null, localPos, null, fixtureColorNum, null, false, newSymbolType, tempLineUUID, currentRotation, id
    );
    newFixture.userData.id = id;
    newFixture.userData.basePoint = oldBasePoint || { x: localPos.x, y: localPos.y };
    newFixture.userData.tempLineUUID = tempLineUUID;
    newFixture.userData.power = power;
    newFixture.userData.symbol = newSymbolType;
    newFixture.userData.rotate = currentRotation;
    if (oldType != null) newFixture.userData.fixtureType = oldType;
    if (oldLampPower != null) newFixture.userData.lampPower = oldLampPower;
    if (oldLampCount != null) newFixture.userData.lampCount = oldLampCount;
    if (oldDesc != null) newFixture.userData.description = oldDesc;
    parent.add(newFixture);

    // 5) Yeni fixture default text (B2/10W) ile çizildi → eski değerlere güncelle
    const newTextGroup = newFixture.getObjectByName('textGroup');
    if (newTextGroup) {
      if (oldType != null) updateTextMesh(newTextGroup, 'textType', String(oldType));
      if (power != null) updateTextMesh(newTextGroup, 'textPower', String(power));
      if (oldDesc) updateTextMesh(newTextGroup, 'textDesc', String(oldDesc));
    }

    setDetails(prev => ({ ...prev, symbol: newSymbolType }));
    // 6) Referansları yenile — sonraki güç/tip/açıklama değişimleri yeni fixture'a gitsin
    fixture = newFixture;
    textGroup = newTextGroup;
  };

  const handleSelectCount = (val) => {
    const lampCount = val.target.value
    const power = lampCount === 1 ? power : lampCount * details.lampPower

    setDetails(prev => ({
      ...prev,
      lampCount: lampCount,
      power: power
    }))
    fixture.userData.lampCount = lampCount
    updateTextMesh(textGroup, 'textPower', lampCount > 1 ? lampCount + "x" + details.lampPower + "W" : power + "W");

  };

  const handleSelectLampPower = (val) => {
    const lampPower = val.target.value
    const power = lampPower * details.lampCount
    setDetails(prev => ({
      ...prev,
      lampPower: lampPower,
      power: power
    }))
    const powerText = details.lampCount === 1 ? power + "W" : details.lampCount + "x" + lampPower + "W"
    fixture.userData.lampPower = lampPower
    fixture.userData.power = power
    updateTextMesh(textGroup, 'textPower', powerText);

  };

  const handleSelectType = (e) => {
    const newType = e.target.value;
    setDetails(prev => ({ ...prev, fixtureType: newType }));
    fixture.userData.fixtureType = newType
    updateTextMesh(textGroup, 'textType', newType);
  };

  useEffect(() => {
  }, [details]);

  return (
    <div
      ref={menuRef} className="panel-type-menu" style={{ left: x, top: y }}
    >
      <div
        onMouseDown={handleMouseDownWithStop}
        className="panel-type-menu-header"
      >
        Armatür Detay
        <button
          className="close-button"
          onClick={e => { e.stopPropagation(); onClose(); }}
        >
          ×
        </button>
      </div>
      <div className="panel-type-options">
        <div className="panel-type-options">
          <div className="panel-type-details-box">
            <table className="panel-type-details-table">
              <tbody>
                <tr  >
                  <td className="td"> Tip </td>
                  <td>
                    <select style={{ 
                        width: "100%", 
                        maxWidth: "160px",
                        backgroundColor: '#333',
                        color: '#eee',
                        border: '1px solid #555',
                        borderRadius: '4px',
                        padding: '2px'
                      }}
                      onChange={e => handleSelectType(e)}
                      value={details.fixtureType}
                    >
                      <option value="A">A  </option>
                      <option value="B1">B1</option>
                      <option value="B2">B2</option>
                      <option value="C">C  </option>
                      <option value="E">E  </option>
                      <option value="G">G  </option>
                      <option value="H">H  </option>
                      <option value="L">L  </option>
                      <option value="N">N  </option>
                      <option value="O">O  </option>
                    </select>
                  </td>
                </tr>
                {details.fixtureType === "N" ?
                  <tr className="label-cell">
                    <td className="label-cell"> Lamba Sayısı </td>
                    <td className="label-cell">
                      <select style={{ 
                          width: "100%", 
                          maxWidth: "160px",
                          backgroundColor: '#333',
                          color: '#eee',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          padding: '2px'
                        }}
                        onChange={e => handleSelectCount(e)}
                        value={details.lampCount}
                      >
                        {Array.from({ length: 30 }, (_, i) => {
                          const val = i + 1;
                          return (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                  </tr>
                  : null}
                <tr className="label-cell">
                  <td className="label-cell"> Güç </td>
                  <td className="label-cell">
                    <select style={{ 
                        width: "100%", 
                        maxWidth: "160px",
                        backgroundColor: '#333',
                        color: '#eee',
                        border: '1px solid #555',
                        borderRadius: '4px',
                        padding: '2px'
                      }}
                      onChange={e => handleSelectLampPower(e)}
                      value={details.power}
                    >
                      <option value={10}>10W  </option>
                      <option value={15}>15W  </option>
                      <option value={18}>18W  </option>
                      <option value={20}>20W  </option>
                      <option value={30}>30W  </option>
                      <option value={40}>40W  </option>
                      <option value={50}>50W  </option>
                      <option value={60}>60W  </option>
                      <option value={80}>80W  </option>
                      <option value={100}>100W  </option>
                      <option value={150}>150W  </option>
                      <option value={200}>200W  </option>
                      <option value={250}>250W  </option>
                    </select>
                  </td>
                </tr>
                <tr className="label-cell">
                  <td className="label-cell">Armatür Türü</td>
                  <td>
                    <select style={{ 
                        width: "100%", 
                        maxWidth: "160px",
                        backgroundColor: '#333',
                        color: '#eee',
                        border: '1px solid #555',
                        borderRadius: '4px',
                        padding: '2px'
                      }}
                      onChange={e => handleSelectSymbol(e)}
                      value={details.symbol}
                    >
                      <option value="normal">Normal  </option>
                      <option value="etanj">Etanj  </option>
                      <option value="Kare Led Spot">Kare Led Spot  </option>
                      <option value="Asma Tavan Kare Floresans">Asma Tavan Kare Floresans  </option>
                      <option value="Yuvarlak Led Spot">Yuvarlak Led Spot  </option>
                      <option value="tablo">Tablo  </option>
                      <option value="aplik">Aplik  </option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <label className='labelTextFixture'>Tip : {details.fixtureType} </label>
          <label className='labelTextFixture'>Güç :{details.lampCount > 1 ?
            details.lampCount + "x" + details.lampPower + "=" + details.power + "W"
            : details.power + "W"}
          </label>
          <label className='labelTextFixture'></label>
          <label className='labelTextFixture'></label>
        </div>
      </div>
    </div>
  );
}

